import React, { useState } from 'react';
import { LogOut, HelpCircle, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const BRAND_NAME = 'Himil Fleet';
const BRAND_LOGO_SRC = '/Himil-logo.png';

/**
 * Public-style chrome for applicant pages: slim header with the company name,
 * an optional stepper strip, a constrained body, and room for a sticky footer.
 */
const ApplicationShell = ({ companyName, subtitle = 'Driver Application', stepper = null, children, showAccount = true, maxWidth = 'max-w-4xl' }) => {
  const { user, userData, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const displayName = userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : (user?.email || '');

  return (
    <div className="min-h-screen bg-gray-50 pb-44 sm:pb-32">
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur">
        <div className={`${maxWidth} mx-auto flex h-14 items-center justify-between px-4 sm:px-6`}>
          <div className="flex min-w-0 items-center gap-3">
            <img src={BRAND_LOGO_SRC} alt={`${BRAND_NAME} logo`} className="h-8 w-8 flex-shrink-0" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900">{companyName || BRAND_NAME}</p>
              <p className="truncate text-xs text-gray-500">{subtitle}</p>
            </div>
          </div>
          {showAccount && user && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                className="inline-flex max-w-[180px] items-center gap-1 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <span className="truncate">{displayName}</span>
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
              </button>
              {menuOpen && (
                <div role="menu" className="absolute right-0 mt-1 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                  <p className="px-3 py-2 text-xs text-gray-500">Your progress is saved automatically. You can sign out and come back any time.</p>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); logout(); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <LogOut className="h-4 w-4" aria-hidden="true" /> Save and sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {stepper && (
          <div className={`${maxWidth} mx-auto px-4 pb-3 pt-2 sm:px-6 sm:pt-4`}>
            {stepper}
          </div>
        )}
      </header>

      <main className={`${maxWidth} mx-auto px-4 py-5 sm:px-6 sm:py-8`}>
        {children}
      </main>

      <footer className={`${maxWidth} mx-auto px-4 pb-6 text-center text-xs text-gray-500 sm:px-6`}>
        <p className="inline-flex items-center gap-1">
          <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
          Questions about this application? Contact {companyName || 'the company'} directly.
        </p>
      </footer>
    </div>
  );
};

export default ApplicationShell;
